import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import AuthTenantLoader from '../../../loaders/AuthTenantLoader';
import AuthTenantPageError from '../../../error/AuthTenantPageError';
import * as app_consts from "../../../../utils/app_consts";
import { tenant_auth_actions_helper } from '../../../../actions/actions_helper';
import { useDispatch } from 'react-redux';
import { RETRIEVE_TENANT_STOCK_AGE_ANALYSIS_SUMMARY_API } from '../../../../api/api_data';
import { fail_retrieve_tenant_stock_age_analysis_summary, loading_retrieve_tenant_stock_age_analysis_summary, reset_retrieve_tenant_stock_age_analysis_summary, success_retrieve_tenant_stock_age_analysis_summary } from '../../../../reducers/auth_tenant/retrieve_tenant_stock_age_analysis_summary_Slice';
import { COULD_NOT_RETRIEVE_STOCK_AGE_ANALYSIS_SUMMARY_ERROR_MSG, COULD_NOT_RETRIEVE_STOCK_AGE_ANALYSIS_SUMMARY_SUCCESS_MSG } from '../../../../utils/error_success_messages';
import { useSelector } from 'react-redux';
import { formatNumber, messageToast } from '../../../../utils/Utils';
import { Button, Col, Icon, Row } from '../../../../components/Component';
import { Card } from 'reactstrap';
import AuthTenantNoData from '../../../error/AuthTenantNoData';
import { Pie as ReactChartPie } from "react-chartjs-2";
import { download, generateCsv, mkConfig } from 'export-to-csv';
import moment from 'moment';
import StatCard from '../../../../components/StatCard';

const StockAgeAnalysisReportsSummary = ({ tenant_info }) => {

 const [loading, setLoading] = useState(true);
 const [error, setError] = useState("");
 const [page_data, setPageData] = useState();
 const dispatch = useDispatch();

 useEffect(() => {
  retrieve_data();
 }, []);

 // Retrieve Data
 const retrieve_data = () => {
  setError("");

  // Dispatch
  dispatch(tenant_auth_actions_helper(
   loading_retrieve_tenant_stock_age_analysis_summary,
   success_retrieve_tenant_stock_age_analysis_summary,
   fail_retrieve_tenant_stock_age_analysis_summary,
   COULD_NOT_RETRIEVE_STOCK_AGE_ANALYSIS_SUMMARY_ERROR_MSG,
   COULD_NOT_RETRIEVE_STOCK_AGE_ANALYSIS_SUMMARY_SUCCESS_MSG,
   app_consts.GET,
   `/${tenant_info?.url_prefix}/${RETRIEVE_TENANT_STOCK_AGE_ANALYSIS_SUMMARY_API}`
  ));
 };

 const retrieve_tenant_stock_age_analysis_summary_listener = useSelector((state) => state.retrieve_tenant_stock_age_analysis_summary.retrieve_tenant_stock_age_analysis_summary);

 useEffect(() => {
  if (retrieve_tenant_stock_age_analysis_summary_listener?.status !== app_consts.IDLE)
  {
   if (retrieve_tenant_stock_age_analysis_summary_listener?.status === app_consts.LOADING)
   {
    setError("");
    setLoading(true);
   } else
   {
    if (retrieve_tenant_stock_age_analysis_summary_listener?.status === app_consts.FAIL)
    {
     messageToast(retrieve_tenant_stock_age_analysis_summary_listener.message, app_consts.FAIL);
     setError(retrieve_tenant_stock_age_analysis_summary_listener?.message);
    }
    if (retrieve_tenant_stock_age_analysis_summary_listener?.status === app_consts.SUCCESS)
    {
     setPageData(retrieve_tenant_stock_age_analysis_summary_listener?.data);
    }
    dispatch(reset_retrieve_tenant_stock_age_analysis_summary());
    setLoading(false);
   }
  }
 }, [retrieve_tenant_stock_age_analysis_summary_listener]);

 const inOutDistributionData = {
  labels: ["Stock Added", "Stock Subtracted"],
  datasets: [
   {
    data: [page_data?.total_added_stock, page_data?.total_subtracted_stock],
    backgroundColor: [
     "hsl(140, 60%, 60%)", // green for added
     "hsl(10, 70%, 65%)",  // red for removed
    ],
    borderWidth: 1,
   },
  ],
 };

 const exportInOutDistributionData = () => {

  if (!page_data?.grand_total)
  {
   console.warn("No In and Out data available for export.");
   return;
  }

  const end = moment().format("YYYY-MM-DD HH:mm:ss");

  const csvData = [{
   products_count: page_data?.products_count,
   total_added_stock: page_data?.total_added_stock,
   total_subtracted_stock: page_data?.total_subtracted_stock,
   grand_total: page_data?.grand_total,
   added_percentage: page_data?.added_percentage,
   subtracted_percentage: page_data?.subtracted_percentage
  }];

  const csvConfig = mkConfig({
   filename: `stock_movement_distribution_as of_${end}`,
   fieldSeparator: ",",
   showLabels: true,
   useBom: true,
   useKeysAsHeaders: true,
  });

  const csv = generateCsv(csvConfig)(csvData);
  download(csvConfig)(csv);
 };

 const inOutChartOptions = {
  cutout: "50%", // donut style
  plugins: {
   legend: {
    position: "bottom",
    labels: {
     font: { size: 13 },
     color: "#555",
     generateLabels: (chart) => {
      const dataset = chart.data.datasets[0];
      return chart.data.labels.map((label, i) => {
       const value = dataset.data[i];
       return {
        text: `${label} – ${formatNumber(value)} Units (${formatNumber((value / page_data?.grand_total) * 100)}%)`,
        fillStyle: dataset.backgroundColor[i],
        strokeStyle: dataset.borderColor?.[i],
        lineWidth: 1,
        hidden: chart.getDataVisibility(i) === false,
        index: i,
       };
      });
     },
    },
   },
   tooltip: {
    callbacks: {
     label: (context) => {
      const label = context.label ?? "";
      const value = context.parsed ?? 0;
      const total = context.dataset.data.reduce((acc, val) => acc + val, 0);
      const percentage = ((value / total) * 100).toFixed(1);
      return `${label}: ${value} Orders (${percentage}%)`;
     },
    },
   },
  },
  maintainAspectRatio: false,
 };

 return (
  <>

   {
    loading ? (
     <AuthTenantLoader tenant_info={tenant_info} loading_message="Loading Stock Analysis Reports Summary..." />
    ) : error ? (
     <AuthTenantPageError tenant_info={tenant_info} error_message={error} />
    ) : (
     <>
      <motion.div
       initial={{ opacity: 0, y: -10 }}
       animate={{ opacity: 1, y: 0 }}
       transition={{ duration: 0.3 }}
      >
       <motion.div
        className="bg-light border-start border-primary ps-3 py-2 mb-3 rounded"
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.4, delay: 0.1 }}
       >
        <p className="text-muted mb-0" style={{ fontSize: "14px" }}>
         Kindly Note that this report Includes or In and Out Stock in the Inventory System.
        </p>
       </motion.div>
      </motion.div>

      <Row className="g-gs">
       <Col xxl="4" sm="6">
        <Card className="card-full overflow-hidden">
         <div className="nk-ecwg nk-ecwg4 h-100">
          {page_data?.grand_total ? (
           <>
            <div className="card-inner flex-grow-1">
             <div className="card-title-group mb-4">
              <div className="card-title">
               <h6 className="title">In and Out Stock Take Data</h6>
              </div>
             </div>
             <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "10px" }}>
              <Button color="primary" onClick={exportInOutDistributionData}>
               Export Distribution CSV
              </Button>
             </div>
             <div className="data-group">
              <div className="nk-ecwg4-ck w-100" style={{ height: '320px', minHeight: '320px' }}>
               {page_data?.grand_total && (
                <ReactChartPie
                 data={inOutDistributionData}
                 options={{
                  ...inOutChartOptions,
                  responsive: true,
                  maintainAspectRatio: false
                 }}
                />
               )}
              </div>
             </div>
            </div>
           </>
          ) : (
           <AuthTenantNoData tenant_info={tenant_info} no_data_message="No Stock Age Analysis Data Found." />
          )}
          <div className="card-inner card-inner-md bg-light">
           <div className="card-note flex items-center gap-2">
            <Icon className="info-fill text-blue-500" />
            <span>Distribution of Total Stock on the Platform.</span>
           </div>
          </div>
         </div>
        </Card>
       </Col>

       {/*  */}

       <Col xxl="8" sm="6">
        <Card className="card-full overflow-hidden">
         <div className="nk-ecwg nk-ecwg4 h-100">
          <div className="card-inner flex-grow-1">
           <div className="card-title-group mb-4">
            <div className="card-title">
             <h6 className="title">Stock Statistics</h6>
            </div>
           </div>
           <div className="data-group">
            <div
             className="nk-ecwg4-ck w-100"
             style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
              gap: "12px",
              padding: "10px"
             }}
            >
             <StatCard
              label="Most Added Brand"
              name={page_data?.most_added_brand?.category?.replace(/_/g, " ").toUpperCase() || "--"}
              units={formatNumber(page_data?.most_added_brand?.total_units || 0)}
              icon="arrow-up-circle"       // Icon showing growth/positive addition
              color="#22c55e"
              bg="#e8f7ee"
             />

             <StatCard
              label="Most Subtracted Brand"
              name={page_data?.most_subtracted_brand?.category?.replace(/_/g, " ").toUpperCase() || "--"}
              units={formatNumber(page_data?.most_subtracted_brand?.total_units || 0)}
              icon="arrow-down-circle"     // Icon for decrease/removal
              color="#ef4444"
              bg="#fdecec"
             />

             <StatCard
              label="Most Frequently Added"
              name={page_data?.most_frequently_added_brand?.category?.replace(/_/g, " ").toUpperCase() || "--"}
              units={formatNumber(page_data?.most_frequently_added_brand?.times_added || 0)}
              icon="repeat"                // Shows repetition/frequency
              color="#3b82f6"
              bg="#eef4ff"
             />

             <StatCard
              label="Most Frequently Subtracted"
              name={page_data?.most_frequently_subtracted_brand?.category?.replace(/_/g, " ").toUpperCase() || "--"}
              units={formatNumber(page_data?.most_frequently_subtracted_brand?.times_added || 0)}
              icon="repeat-fill"           // Indicates repeated removal
              color="#f97316"
              bg="#fff4e6"
             />

             <StatCard
              label="Least Added Brand"
              name={page_data?.least_added_brand?.category?.replace(/_/g, " ").toUpperCase() || "--"}
              units={formatNumber(page_data?.least_added_brand?.total_units || 0)}
              icon="arrow-up-left"         // Small or minimal addition
              color="#22c55e"
              bg="#e8f7ee"
             />

             <StatCard
              label="Least Subtracted Brand"
              name={page_data?.least_subtracted_brand?.category?.replace(/_/g, " ").toUpperCase() || "--"}
              units={formatNumber(page_data?.least_subtracted_brand?.total_units || 0)}
              icon="arrow-down-left"       // Small or minimal subtraction
              color="#ef4444"
              bg="#fdecec"
             />

             <StatCard
              label="Recently Added Product"
              name={page_data?.recently_added_product?.product_name?.replace(/_/g, " ").toUpperCase() || "--"}
              units={formatNumber(page_data?.recently_added_product?.units || 0)}
              icon="plus-circle"           // Represents new addition
              color="#3b82f6"
              bg="#eef4ff"
             />

             <StatCard
              label="Recently Subtracted Product"
              name={page_data?.recently_subtracted_product?.product_name?.replace(/_/g, " ").toUpperCase() || "--"}
              units={formatNumber(page_data?.recently_subtracted_product?.units || 0)}
              icon="minus-circle"          // Represents recent removal
              color="#ef4444"
              bg="#fdecec"
             />


            </div>
           </div>
          </div>
         </div>
        </Card>
       </Col>
      </Row>
     </>
    )
   }

  </>
 );
};

export default StockAgeAnalysisReportsSummary;